import { useMemo } from 'react';
import { catalogHas, findCatalogItem, useCatalogItems, type CatalogEntry } from './catalogItems';
import { formatPartName } from './formatNames';
import { sizeForCategory } from './formatUnits';
import type { PartItem } from './partsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// WHICH catalog row a part is, and WHAT it is called. One answer, read from four places:
// the parts table, the generator, the 3D card and the part selector above all three.
//
// It was written out in each of them, and they had drifted in the way copies do — same
// intent, different order of fallbacks — so a part could be named one thing in the table
// and another in the selector that opens it. The rule is one rule: the extraction's own
// match if it still names a row, else a live lookup by name; and the DATABASE's name in
// the read language when there is a row, else the model's, else the printed one.

// The catalog item this part IS, or null.
//
// `claimed` is a caller's own better claim — a save made in this session, which the
// extraction that ran before it cannot know about. It is a claim like any other and is
// checked against the catalog the same way.
//
// Before the catalog is READ, the claim stands as it is: a list nobody has fetched yet is
// not evidence that a row is missing, and treating it as one would blink every match to
// "not in the database" on load. Callers that must not act on an unverified id (attaching
// a photo to a row that may be gone) check `loaded` themselves.
export function partCatalogId(
  catalog: CatalogEntry[],
  loaded: boolean,
  p: PartItem,
  claimed: number | null = null,
): number | null {
  const claim = claimed ?? p.catalogId ?? null;
  if (!loaded) return claim;
  return catalogHas(catalog, claim)
    ? claim
    : findCatalogItem(catalog, { ...p, nameEn: p.nameEn || p.name });
}

// THE name, in the language being read: the catalog's when the database knows the part —
// so one part keeps one name across every manual it turns up in — then the model's worked
// out name, then the printed one, unshouted.
export function partDbName(
  catalog: CatalogEntry[],
  loaded: boolean,
  language: string,
  p: PartItem,
): string {
  const id = partCatalogId(catalog, loaded, p);
  const item = loaded && id != null ? catalog.find((c) => c.id === id) : undefined;
  if (item) return (language === 'fr' ? item.nameFr : item.nameEn) || item.nameEn || item.nameFr;
  return (language === 'fr' ? p.nameFr : p.nameEn) || formatPartName(p.name ?? '');
}

export type PartIdentity = { itemId: number | null; name: string };

// Both answers for every part of a list, indexed like it.
export function usePartIdentities(items: PartItem[]): PartIdentity[] {
  const { language } = useLanguage();
  const { items: catalog, loaded } = useCatalogItems();
  return useMemo(
    () =>
      items.map((p) => ({
        itemId: partCatalogId(catalog, loaded, p),
        name: partDbName(catalog, loaded, language, p),
      })),
    [items, catalog, loaded, language],
  );
}

// AND THE SIZE TO MEASURE ITS MESH AGAINST: the manual's, or the database's where the manual
// has none.
//
// The same rule as the name above, for the same reason. A reconstruction is normalised to
// about a unit across, so the millimetres on its labels are the part's size divided by the
// mesh's extents (see mmScale) — and read off the parts list alone, that anchor is rebuilt by
// every press of Phase 2. A re-list writes a fresh part with no size, and a mesh nobody
// touched reads 0 mm.
//
// The catalog row is the copy that survives: same part, its size pushed there by
// fillCatalogSizes as soon as any manual reads one, and no phase rewrites it. The part's own
// still wins where it has one — it is THIS manual's reading of THIS part.
//
// Here rather than at the three viewers that need it, which is where it was going: Phase 2's
// part card, the assembly's parts and the assembly's tools each joined a part to a size, and
// three copies of a fallback are three chances for one of them to keep showing 0 mm.
export function partDbSize(
  catalog: CatalogEntry[],
  itemId: number | null,
  size: PartItem['size'],
): PartItem['size'] {
  if (hasAnyMeasure(size) || itemId == null) return size;
  return catalog.find((c) => c.id === itemId)?.size ?? size;
}

// WHETHER A SIZE RECORD HOLDS A MEASUREMENT AT ALL. A part that has never been measured does
// not carry `null` — it carries an object of fifteen nulls (see NO_SIZE), which is not the
// same thing to a `??` and is exactly the same thing to a reader. Any real number anywhere in
// it counts: a mesh's anchor is one axis, and which one depends on the part's category.
export const hasAnyMeasure = (size: PartItem['size']): boolean =>
  !!size && Object.entries(size).some(
    ([k, v]) => k !== 'source' && k !== 'note' && typeof v === 'number',
  );

// A PART'S SIZE AS ITS MODEL IS BUILT TO: this manual's reading, else the database's (see
// partDbSize), as its category describes it (see sizeForCategory). One rule for the part's own
// build and for the hardware set it is built in, which must tell the model the same numbers.
export const partBuildSize = (
  catalog: CatalogEntry[], itemId: number | null, p: PartItem,
): PartItem['size'] => sizeForCategory(partDbSize(catalog, itemId, p.size), p.category ?? '');
